import React from 'react';
import {
  Box,
  Typography,
  Card,
  Chip,
  Avatar,
  Divider,
  Alert,
  Tooltip,
  IconButton,
} from '@mui/material';
import {
  FavoriteBorder as LikeIcon,
  ChatBubbleOutline as CommentIcon,
  Share as ShareIcon,
  OpenInNew as OpenInNewIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import { getPlatform, getPlatformColor, getPlatformName, platformPostUrl } from '../config/platforms';
import { parseRejection, resultMeta } from '../config/postStatus';
import { thumbnailUrl } from '../utils/media';

export interface PreviewResult {
  platform: string;
  platform_post_id?: string;
  permalink?: string | null;
  status: 'success' | 'failed' | 'rejected';
  error_message?: string;
}

interface PostPreviewProps {
  content: string;
  platforms: string[];
  /**
   * Per-platform caption overrides, keyed by platform id.
   *
   * Without these the preview measures the main caption against every limit — so a post with
   * a deliberately shorter X version still reported the long body as over X's 280, which is
   * both wrong and alarming.
   */
  contentVariants?: Record<string, string> | null;
  mediaUrls?: string[];
  /** Per-platform images, set by a retry with a replacement. Absent platforms use `mediaUrls`. */
  mediaVariants?: Record<string, string[]> | null;
  /** Publish outcomes, when the post has already gone out. */
  results?: PreviewResult[];
  /** Where each platform would publish, from GET /api/platforms. */
  targets?: Record<string, string | null>;
}

/**
 * Approximate how a post will read on each network.
 *
 * This is deliberately a readability check, not a pixel-accurate mock: it shows the real
 * text against the real character limit and the account it will publish as, so an
 * over-long or awkwardly-truncated post is visible before it goes out. Every platform
 * renders its own way and that is not worth chasing.
 */
export const PostPreview: React.FC<PostPreviewProps> = ({
  content,
  platforms,
  contentVariants = null,
  mediaUrls: sharedMedia = [],
  mediaVariants = null,
  results = [],
  targets = {},
}) => {
  /** The text this platform will actually receive — its override, or the main caption. */
  const textFor = (platformId: string) => {
    const override = contentVariants?.[platformId];
    return override && override.trim() ? override : content;
  };

  if (platforms.length === 0) {
    return (
      <Alert severity="info">Select a platform to see how the post will look.</Alert>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {platforms.map(platformId => {
        const config = getPlatform(platformId);
        const limit = config?.charLimit ?? null;
        // This platform's own text, so an override is measured against its own limit
        const platformText = textFor(platformId);
        const hasOwnText = platformText !== content;
        const overBy = limit ? platformText.length - limit : 0;
        const result = results.find(r => r.platform === platformId);
        const url = platformPostUrl(platformId, result?.platform_post_id, result?.permalink);
        const target = targets[platformId];
        // This platform's own images, so a replacement sent to Instagram alone shows there
        const mediaUrls = mediaVariants?.[platformId]?.length ? mediaVariants[platformId] : sharedMedia;

        // What the platform would actually show if it hard-truncates
        const willTruncate = limit !== null && platformText.length > limit;
        const shown = willTruncate ? platformText.slice(0, limit) : platformText;

        // Media is only attached where the platform accepts it
        const showsMedia = mediaUrls.length > 0 && config?.supportsMedia !== false;

        return (
          <Card key={platformId} variant="outlined" sx={{ overflow: 'hidden' }}>
            <Box
              sx={{
                px: 2,
                py: 1,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                bgcolor: getPlatformColor(platformId),
                color: '#fff',
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 600, flexGrow: 1 }}>
                {getPlatformName(platformId)}
              </Typography>

              {result && (
                <Chip
                  label={result.status === 'rejected' ? 'Not sent' : resultMeta(result.status)?.label}
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: '0.7rem',
                    bgcolor: 'rgba(255,255,255,0.25)',
                    color: '#fff',
                  }}
                />
              )}

              {url && (
                <Tooltip title="Open the live post">
                  <Box
                    component="a"
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ color: '#fff', display: 'flex' }}
                  >
                    <OpenInNewIcon sx={{ fontSize: 16 }} />
                  </Box>
                </Tooltip>
              )}
            </Box>

            <Box sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', gap: 1.5, mb: 1.5 }}>
                <Avatar sx={{ width: 36, height: 36, bgcolor: getPlatformColor(platformId) }}>
                  {(target || getPlatformName(platformId))[0]?.toUpperCase()}
                </Avatar>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {target || 'Not connected'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {/* Do not call an unsent post "Published" — that was the whole problem */}
                    {result
                      ? result.status === 'success'
                        ? 'Published'
                        : result.status === 'rejected' ? 'Not sent' : 'Failed'
                      : 'Preview'}
                  </Typography>
                </Box>
              </Box>

              <Typography
                variant="body2"
                sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', lineHeight: 1.6 }}
              >
                {shown}
                {willTruncate && (
                  <Box component="span" sx={{ color: 'error.main', fontWeight: 600 }}>
                    {' '}… {overBy.toLocaleString()} characters beyond this platform's limit
                  </Box>
                )}
              </Typography>

              {showsMedia && (
                <Box sx={{ mt: 1.5, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {mediaUrls.map(url2 => (
                    <Box key={url2} sx={{ position: 'relative', '&:hover .dl': { opacity: 1 } }}>
                      <Box
                        component="img"
                        // 640 rather than 320: this renders up to 260px tall, and a 320px
                        // source would be visibly soft on a high-density screen.
                        src={thumbnailUrl(url2, 640)}
                        alt=""
                        loading="lazy"
                        decoding="async"
                        sx={{
                          maxWidth: '100%',
                          maxHeight: 260,
                          borderRadius: 1,
                          border: 1,
                          borderColor: 'divider',
                          display: 'block',
                        }}
                      />

                      {/* The preview is a resized copy — this points at the untouched upload,
                          the same file the platforms receive. There was no way to reach it
                          from the UI before.
                          `download` rather than a plain link so it saves rather than opening
                          a tab the person then has to save from. */}
                      <Tooltip title="Download the original, full-size file" arrow>
                        <IconButton
                          className="dl"
                          component="a"
                          href={url2}
                          download
                          size="small"
                          // The preview sits inside clickable cards in some views; without
                          // this a download would also open the post.
                          onClick={e => e.stopPropagation()}
                          sx={{
                            position: 'absolute',
                            top: 6,
                            right: 6,
                            bgcolor: 'rgba(0,0,0,0.55)',
                            color: '#fff',
                            // Always visible on touch, where there is no hover to reveal it.
                            opacity: { xs: 1, md: 0 },
                            transition: 'opacity 0.15s',
                            '&:hover': { bgcolor: 'rgba(0,0,0,0.78)' },
                          }}
                        >
                          <DownloadIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  ))}
                </Box>
              )}

              {mediaUrls.length > 0 && config?.supportsMedia === false && (
                <Alert severity="info" sx={{ mt: 1.5, py: 0, fontSize: '0.8rem' }}>
                  {getPlatformName(platformId)} posts as text only here — the attached media
                  will not appear.
                </Alert>
              )}

              {config?.requiresMedia && mediaUrls.length === 0 && (
                <Alert severity="warning" sx={{ mt: 1.5, py: 0, fontSize: '0.8rem' }}>
                  {getPlatformName(platformId)} requires an image or video.
                </Alert>
              )}

              {/* A rejection is a warning rather than an error: nothing was sent, and the
                  reason is something the author can fix. A failure is the platform's own
                  refusal, which they usually cannot. */}
              {result && result.status !== 'success' && result.error_message && (
                <Alert
                  severity={result.status === 'rejected' ? 'warning' : 'error'}
                  sx={{ mt: 1.5, py: 0, fontSize: '0.8rem' }}
                >
                  {result.status === 'rejected' && <strong>Not sent — </strong>}
                  {parseRejection(result.error_message).text}
                </Alert>
              )}

              <Divider sx={{ my: 1.5 }} />

              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', gap: 2, color: 'text.disabled' }}>
                  <LikeIcon sx={{ fontSize: 16 }} />
                  <CommentIcon sx={{ fontSize: 16 }} />
                  <ShareIcon sx={{ fontSize: 16 }} />
                </Box>
                <Typography
                  variant="caption"
                  color={willTruncate ? 'error.main' : 'text.secondary'}
                >
                  {/* This platform's own count. Showing the main caption's length here was
                      the bug: a post with a shorter X version read "364 / 280". */}
                  {platformText.length.toLocaleString()}
                  {limit !== null && ` / ${limit.toLocaleString()}`}
                  {hasOwnText && ' · own version'}
                </Typography>
              </Box>
            </Box>
          </Card>
        );
      })}
    </Box>
  );
};
